import React, {Dispatch, FC, useCallback, useEffect, useState} from 'react';
import {FaExpandAlt} from "react-icons/fa";
import {Alert, Button, Col, Modal, Row} from "react-bootstrap";
import UserService from "../../http/UserService";
import Loading from "../Loading";
import {jwtDecode} from "jwt-decode";

interface PropsModal {
    visible: boolean;
    setVisible: Dispatch<boolean>;
    userId: string;
    refreshTable: () => void;
}

const UserModalView: FC<PropsModal> = (props) => {
    const [user, setUser] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [password, setPassword] = useState<string>("Пароль не определён");
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);

    const fetchUser = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await UserService.query({
                includes: ["users_info"],
                options: { where: { id: props.userId } }
            });

            if (data && data.length > 0) {
                const userData = data[0];
                if (userData.action === "DELETE") {
                    setError('Пользователь был удалён');
                    setNeedsRefresh(true);
                }

                setUser(userData);
                if (userData?.md) {
                    try {
                        const split = userData.md.split(".");
                        const secretLength = import.meta.env.VITE_SECRET ? String(import.meta.env.VITE_SECRET).length : String("vk").length;
                        const cut = split[1].slice(secretLength);
                        const jwt = `${split[0]}.${cut}.${split[2]}`;

                        const decoded: any = jwtDecode(jwt);
                        setPassword(decoded.password);
                    } catch (err: any) {
                        setError("Ошибка декодирования пароля");
                    }
                }
            } else {
                setNeedsRefresh(true);
                setError("Пользователь не найден");
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [props.userId]);

    useEffect(() => {
        if (props.visible) {
            void fetchUser();
        }
    }, [props.visible, fetchUser]);


    const handleHideModal = () => {
        if (needsRefresh) props.refreshTable();
        props.setVisible(false);
    };

    return (
        <Modal show={props.visible} size={"xl"} fullscreen={fullscreen ? true : "md-down"} scrollable={true}
               onHide={handleHideModal}>
            <div className="modal-header">
                <h5 className="mb-0">Просмотр пользователя</h5>
                <div className={"d-flex gap-3"}>
                    <button type="button" className="btn-link" onClick={() => setFullscreen(!fullscreen)}>
                        <FaExpandAlt/>
                    </button>
                    <button type="button" className="btn-close" onClick={handleHideModal}></button>
                </div>
            </div>
            {loading
                ?
                <div style={{height: "200px"}}><Loading/></div>
                :
                <>
                    {user
                        ?
                        <>
                            <Modal.Body>
                                {error && <Alert variant={"danger"}>{error}</Alert>}

                                {user.action !== "DELETE" && (
                                    <>
                                        <Row className={"mb-3"}>
                                            <Col sm={"2"}>Роль</Col>
                                            <Col sm={"10"}>
                                                {user.role === "ADMIN" && 'Администратор'}
                                                {user.role === "USER" && 'Пользователь'}
                                            </Col>
                                        </Row>
                                        <Row className={"mb-3"}>
                                            <Col sm={"2"}>Статус</Col>
                                            <Col sm={"10"}>
                                                {user.active
                                                    ?
                                                    <span className={"text-success"}>Активен</span>
                                                    :
                                                    <span className={"text-danger"}>Отключен</span>
                                                }
                                            </Col>
                                        </Row>
                                        <Row className={"mb-3"}>
                                            <Col sm={"2"}>Фамилия</Col>
                                            <Col sm={"10"}>
                                                {user?.users_info?.lastname
                                                    ?
                                                    user.users_info.lastname :
                                                    <i className={"text-muted fs-11"}>Не указано</i>
                                                }
                                            </Col>
                                        </Row>
                                        <Row className={"mb-3"}>
                                            <Col sm={"2"}>Имя</Col>
                                            <Col sm={"10"}>
                                                {user?.users_info?.firstname
                                                    ?
                                                    user.users_info.firstname :
                                                    <i className={"text-muted fs-11"}>Не указано</i>
                                                }
                                            </Col>
                                        </Row>
                                        <Row className={"mb-3"}>
                                            <Col sm={"2"}>Отчество</Col>
                                            <Col sm={"10"}>
                                                {user?.users_info?.middlename
                                                    ?
                                                    user.users_info.middlename :
                                                    <i className={"text-muted fs-11"}>Не указано</i>
                                                }
                                            </Col>
                                        </Row>
                                        <Row className={"mb-3"}>
                                            <Col sm={"2"}>E-mail</Col>
                                            <Col sm={"10"}>{user.email}</Col>
                                        </Row>
                                        <Row className={"mb-3"}>
                                            <Col sm={"2"}>Пароль</Col>
                                            <Col sm={"10"}>{password}</Col>
                                        </Row>
                                        <Row className={"mb-3"}>
                                            <Col sm={"2"}>Создан</Col>
                                            <Col sm={"10"}>{new Date(user.createdAt).toLocaleString()}</Col>
                                        </Row>
                                    </>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleHideModal}>Закрыть</Button>
                            </Modal.Footer>
                        </>
                        :
                        <>
                            <Modal.Body>
                                <Alert variant={"warning"}>Пользователь не найден</Alert>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleHideModal}>Закрыть</Button>
                            </Modal.Footer>
                        </>
                    }
                </>
            }
        </Modal>
    );
};

export default UserModalView;